import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Loader2, Inbox, ShieldCheck, Copy, CheckCircle2, Flag, StickyNote, Mail, Phone,
  ClipboardList, Trash2, Search, MessageSquareText,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../../components/ui/alert-dialog';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../../components/Shared';

// Which First Response Templates are offered as one-click copies in the inbox
const INBOX_TEMPLATE_KEYS = ['contact-message', 'volunteer-signup', 'privacy-question', 'not-official-city'];

const copyToClipboard = async (text, okMsg) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(okMsg);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    toast.success(okMsg);
  }
};

const MessageCard = ({ msg, templates, onChanged, onDelete }) => {
  const [busy, setBusy] = useState('');
  const [customOpen, setCustomOpen] = useState(false);
  const [custom, setCustom] = useState('');

  const inbox = async (patch, okMsg) => {
    setBusy('inbox');
    try {
      await api.put(`/admin/contact-messages/${msg.id}/inbox`, patch);
      if (okMsg) toast.success(okMsg);
      onChanged();
    } catch { toast.error('Update failed'); }
    setBusy('');
  };

  const saveNote = (note) => {
    if (note === (msg.admin_note || '')) return;
    inbox({ admin_note: note }, 'Private note saved');
  };

  const statusBadges = [];
  if (!msg.read) statusBadges.push(<Badge key="unread" variant="outline" className="bg-amber-50 text-amber-900 border-amber-300 text-[10px] font-bold">Unread</Badge>);
  if (msg.reviewed) statusBadges.push(<Badge key="rev" variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-300 text-[10px] font-bold">Reviewed</Badge>);
  if (msg.needs_follow_up) statusBadges.push(<Badge key="fu" variant="outline" className="bg-red-50 text-red-800 border-red-300 text-[10px] font-bold">Needs Follow-Up</Badge>);

  return (
    <div className={`rounded-xl border bg-white p-4 ${msg.needs_follow_up ? 'border-red-200' : msg.reviewed ? 'border-emerald-200' : 'border-slate-200'}`} data-testid={`inbox-message-${msg.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-serif font-bold text-[#1a2744] text-sm">{msg.name || 'Anonymous'} <span className="font-sans font-normal text-slate-400 text-xs">· {formatDate(msg.created_at)}</span></p>
          <p className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            {msg.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {msg.email}</span>}
            {msg.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {msg.phone}</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5 shrink-0" data-testid={`inbox-status-${msg.id}`}>{statusBadges}</div>
      </div>

      {msg.subject && <p className="text-sm font-bold text-slate-700 mt-2">{msg.subject}</p>}
      <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap" data-testid={`inbox-body-${msg.id}`}>{msg.message}</p>

      {/* Copy actions — copy-only, nothing is ever sent automatically */}
      <div className="mt-3 pt-3 border-t border-slate-100">
        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1"><ClipboardList className="h-3 w-3" /> Copy a reply (paste into email or Facebook — nothing sends automatically)</p>
        <div className="flex flex-wrap gap-1.5">
          {INBOX_TEMPLATE_KEYS.map((k) => {
            const t = templates[k];
            if (!t) return null;
            return (
              <Button key={k} size="sm" variant="outline" onClick={() => copyToClipboard(t.copy, `"${t.title}" copied`)} className="h-8 text-[11px] font-bold btn-press" data-testid={`inbox-copy-${k}-${msg.id}`}>
                <Copy className="h-3 w-3 mr-1" /> {t.title}
              </Button>
            );
          })}
          <Button size="sm" variant="outline" onClick={() => setCustomOpen((v) => !v)} className="h-8 text-[11px] font-bold btn-press border-[#C9A84C] text-[#7A5C12]" data-testid={`inbox-custom-toggle-${msg.id}`}>
            <MessageSquareText className="h-3 w-3 mr-1" /> Custom response
          </Button>
        </div>
        {customOpen && (
          <div className="mt-2">
            <Textarea value={custom} onChange={(e) => setCustom(e.target.value)} rows={4} placeholder={`Hi ${msg.name || 'there'},\n\nThank you for your message…`} className="text-sm" data-testid={`inbox-custom-text-${msg.id}`} />
            <Button size="sm" onClick={() => copyToClipboard(custom, 'Custom response copied')} disabled={!custom.trim()} className="h-8 text-[11px] mt-2 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid={`inbox-custom-copy-${msg.id}`}>
              <Copy className="h-3 w-3 mr-1" /> Copy Custom Response
            </Button>
          </div>
        )}
      </div>

      {/* Review actions */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Button size="sm" onClick={() => inbox({ reviewed: !msg.reviewed }, msg.reviewed ? 'Marked as not reviewed' : 'Marked Reviewed')} disabled={busy === 'inbox'}
          className={`h-8 text-[11px] font-bold btn-press ${msg.reviewed ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-[#1a2744] hover:bg-[#243456] text-white'}`} data-testid={`inbox-reviewed-${msg.id}`}>
          <CheckCircle2 className="h-3 w-3 mr-1" /> {msg.reviewed ? 'Reviewed ✓' : 'Mark Reviewed'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => inbox({ needs_follow_up: !msg.needs_follow_up }, msg.needs_follow_up ? 'Follow-up cleared' : 'Marked Needs Follow-Up')} disabled={busy === 'inbox'}
          className={`h-8 text-[11px] font-bold btn-press ${msg.needs_follow_up ? 'border-red-300 text-red-700 bg-red-50' : ''}`} data-testid={`inbox-followup-${msg.id}`}>
          <Flag className="h-3 w-3 mr-1" /> {msg.needs_follow_up ? 'Needs Follow-Up ✓' : 'Mark Needs Follow-Up'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onDelete(msg)} className="h-8 text-[11px] font-bold text-red-500 hover:text-red-700 hover:bg-red-50 btn-press" data-testid={`inbox-delete-${msg.id}`}>
          <Trash2 className="h-3 w-3 mr-1" /> Delete
        </Button>
      </div>

      {/* Private admin note */}
      <div className="mt-3">
        <label className="text-[11px] font-bold text-slate-500 flex items-center gap-1"><StickyNote className="h-3 w-3" /> Private admin note (never shown publicly)</label>
        <Input defaultValue={msg.admin_note || ''} onBlur={(e) => saveNote(e.target.value)} placeholder="e.g. Replied by email Aug 12 — waiting to hear back…" className="h-10 text-sm mt-1" data-testid={`inbox-note-${msg.id}`} />
      </div>
    </div>
  );
};

export default function AdminContactInbox() {
  const [messages, setMessages] = useState(null);
  const [templates, setTemplates] = useState({});
  const [error, setError] = useState(false);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    api.get('/admin/contact-messages').then((r) => { setMessages(r.data); setError(false); }).catch(() => setError(true));
    api.get('/admin/first-response-templates').then((r) => {
      setTemplates(Object.fromEntries((r.data.templates || []).map((t) => [t.key, t])));
    }).catch(() => setTemplates({}));
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/admin/contact-messages/${deleting.id}`);
      toast.success('Message deleted');
      setDeleting(null);
      load();
    } catch { toast.error('Delete failed'); }
  };

  const filtered = useMemo(() => {
    if (!messages) return [];
    const needle = q.trim().toLowerCase();
    return messages.filter((m) => {
      if (filter === 'unread' && m.read) return false;
      if (filter === 'follow-up' && !m.needs_follow_up) return false;
      if (filter === 'reviewed' && !m.reviewed) return false;
      if (!needle) return true;
      return [m.name, m.email, m.subject, m.message].some((v) => (v || '').toLowerCase().includes(needle));
    });
  }, [messages, q, filter]);

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-center justify-between gap-3" data-testid="inbox-error">
        <p className="text-sm font-semibold text-red-800">Couldn't load contact messages.</p>
        <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold">Retry</Button>
      </div>
    );
  }
  if (!messages) {
    return (
      <div className="space-y-3" data-testid="inbox-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }

  const FILTERS = [
    ['all', `All (${messages.length})`],
    ['unread', `Unread (${messages.filter((m) => !m.read).length})`],
    ['follow-up', `Needs Follow-Up (${messages.filter((m) => m.needs_follow_up).length})`],
    ['reviewed', `Reviewed (${messages.filter((m) => m.reviewed).length})`],
  ];

  return (
    <div data-testid="admin-contact-inbox">
      <h1 className="font-serif font-black text-2xl text-[#1a2744] flex items-center gap-2"><Inbox className="h-6 w-6 text-[#C9A84C]" /> Contact Messages</h1>
      <p className="text-sm text-slate-500 mt-1">Messages from the public Contact form — review, flag, and copy the right reply.</p>

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start mt-3 mb-4" data-testid="inbox-privacy-note">
        <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">Private admin inbox. Copy-only workflow: nothing here sends emails or replies automatically — Steve pastes and sends replies manually. Contact details are never shown publicly.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, subject, message…" className="h-11 pl-9" data-testid="inbox-search" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map(([k, label]) => (
            <Button key={k} size="sm" variant={filter === k ? 'default' : 'outline'} onClick={() => setFilter(k)}
              className={`h-11 text-xs font-bold btn-press ${filter === k ? 'bg-[#1a2744] hover:bg-[#243456] text-white' : ''}`} data-testid={`inbox-filter-${k}`}>
              {label}
            </Button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center" data-testid="inbox-empty">
          <Inbox className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-semibold text-slate-500">{messages.length === 0 ? 'No contact messages yet. New messages from the public Contact form will appear here.' : 'No messages match this filter.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((m) => <MessageCard key={m.id} msg={m} templates={templates} onChanged={load} onDelete={setDeleting} />)}
        </div>
      )}

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="inbox-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-[#1a2744]">Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the message from {deleting?.name || 'this sender'}. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="inbox-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="inbox-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
